// Lifted from schedugram-client/react-src/components/ui/dialog.tsx
// — modal-stack / portal-detection logic dropped (not needed in this app yet);
// FontAwesome icons via the kit script in index.html replace the React-only
// stack. Add the modal-stack provider back if/when nested dialogs land here.

import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as React from "react";
import { cn } from "@/lib/utils";

interface DialogScrollContextValue {
  canScrollUp: boolean;
  canScrollDown: boolean;
  setScrollInfo: (info: { canScrollUp: boolean; canScrollDown: boolean }) => void;
}

const DialogScrollContext = React.createContext<DialogScrollContextValue>({
  canScrollUp: false,
  canScrollDown: false,
  setScrollInfo: () => {},
});

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  contentWrapperClassName,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  contentWrapperClassName?: string;
  showCloseButton?: boolean;
}) {
  const [scrollInfo, setScrollInfo] = React.useState({
    canScrollUp: false,
    canScrollDown: false,
  });

  return (
    <DialogScrollContext.Provider value={{ ...scrollInfo, setScrollInfo }}>
      <DialogPortal data-slot="dialog-portal">
        <DialogOverlay />
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            "fixed top-[50%] left-[50%] z-50 flex max-h-[calc(100vh-2rem)] w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] flex-col overflow-hidden rounded-lg bg-white shadow-lg duration-200",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            className,
          )}
          {...props}
        >
          <div
            className={cn(
              // M12.12 — bumped from px-3 to p-6 so content breathes
              // away from the modal edges. Callers can still override
              // via contentWrapperClassName for bespoke layouts.
              "relative flex flex-col overflow-hidden p-6",
              contentWrapperClassName,
            )}
          >
            {children}
            {showCloseButton && (
              <DialogPrimitive.Close
                data-slot="dialog-close"
                className="absolute top-0 right-2 cursor-pointer p-0 leading-none text-neutral-400 ring-offset-white transition-colors hover:text-neutral-900 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none"
              >
                <i className="fa-regular fa-xmark text-lg" aria-hidden="true" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogScrollContext.Provider>
  );
}

function DialogBody({ className, children, ...props }: React.ComponentProps<"div">) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const { setScrollInfo, canScrollUp, canScrollDown } = React.useContext(DialogScrollContext);

  const updateScrollState = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollUp = el.scrollTop > 0;
    const scrollDown = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    setScrollInfo({ canScrollUp: scrollUp, canScrollDown: scrollDown });
  }, [setScrollInfo]);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    // Feature-detect ResizeObserver — older browsers / some embedded contexts
    // ship without it. Scroll-only updates are an acceptable fallback.
    const RO = (globalThis as { ResizeObserver?: typeof ResizeObserver }).ResizeObserver;
    const resizeObserver = RO ? new RO(updateScrollState) : null;
    resizeObserver?.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      resizeObserver?.disconnect();
    };
  }, [updateScrollState]);

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        data-slot="dialog-body"
        className={cn("flex-1 overflow-y-auto pb-2", className)}
        {...props}
      >
        {children}
      </div>
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute top-0 right-0 left-0 z-10 h-8 bg-gradient-to-b from-black/15 to-transparent transition-opacity duration-150",
          canScrollUp ? "opacity-100" : "opacity-0",
        )}
      />
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute right-0 bottom-0 left-0 z-10 h-8 bg-gradient-to-t from-black/15 to-transparent transition-opacity duration-150",
          canScrollDown ? "opacity-100" : "opacity-0",
        )}
      />
    </div>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      // M12.12 — drop the top padding; DialogContent's p-6 wrapper
      // already gives the header breathing room away from the modal
      // edge. Leaving the padding here pushed the title down into the
      // dialog and left a thin strip at the top without the close-X.
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      // M12.12 — DialogContent now owns bottom padding; drop pb-3
      // here so footer buttons sit flush to the modal's edge
      // padding rather than double-stacking.
      className={cn("flex flex-col-reverse gap-2 pt-4 sm:flex-row sm:justify-end", className)}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("font-gotham text-2xl leading-none font-bold text-neutral-900", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-sm text-neutral-500", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
